// RetoursVenteMobile.jsx — onglet « Retour de vente » en version mobile, CRUD complet.
// Réutilise le singleton retourVenteAPI et le PaymentFormModal de GestionRetourVente.tsx.
//
// Comme le desktop, les lignes proviennent exclusivement des articles de la vente d'origine,
// et les quantités sont plafonnées par ce qui reste RÉELLEMENT retournable
// (/api/orders/:id/returned-quantities) — contrairement au retour d'achat, qui ne déduit pas
// le déjà-retourné (dette technique connue, cf. docs/project-notes/PROMPT-REFONTE-MOBILE-SUITE.md §5).
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Alert, Button, message, Modal } from "antd";
import { useNavigate } from "react-router-dom";
import {
  ProductScreen, Appbar, SearchBar, Chips, EmptyState, Fab, Sheet, ActionRow,
  DetailRow, Note, Field, TextInput, TextAreaInput, SelectInput, Pill, Mono, Ico,
  fmt, fmtMoney, useMobileMenu,
} from "../produits/productMobileKit";
import { useAuth } from "../../context/AuthContext";
import { useSelection } from "../../SelectionContext";
import { retourVenteAPI, PaymentFormModal } from "../../gestion-retour-vente/GestionRetourVente";
import { getSalesTabsForUser } from "../../gestion-vente/SalesTabsLayout";
import { fetchOrderPayments } from "./ventesMobileApi";
import SalesReturnDocumentAction from "../../gestion-vente/components/SalesReturnDocumentAction";
import { findSalesReturnSourceItem, salesReturnUnitFields } from "../../utils/salesReturnItemUnits";
import useSalesReturnItemUnits from "../../gestion-retour-vente/useSalesReturnItemUnits";
import {
  PERM, ORDER_STATUS_OPTIONS, canDeleteReturn, canRefund, clientNameOf, computeTotals,
  fmtDate, isEditBlocked, MIN_RETURN_QUANTITY, num, orderPill, parseDecimal, paymentPill,
  paymentStatusOf,
} from "./retoursVenteMobileShared";

const PAGE = 20;

const FILTERS = [
  { key: "all", label: "Tous", status: null },
  { key: "unpaid", label: "Non payés", status: "Non payé" },
  { key: "partially_paid", label: "Partiels", status: "Partiellement payé" },
  { key: "paid", label: "Payés", status: "Payé" },
];

const idOf = (v) => (v && typeof v === "object" ? v.id : v);
const todayIso = () => new Date().toISOString().slice(0, 10);
const asItems = (d) => (Array.isArray(d) ? d : d?.items || []);

export default function RetoursVenteMobile({ onMenu: onMenuProp }) {
  const outletMenu = useMobileMenu();
  const onMenu = onMenuProp || outletMenu;
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  const { selectedWarehouse, selectedCompany } = useSelection();

  const companyId = idOf(selectedCompany);
  const warehouseId = idOf(selectedWarehouse);

  const can = {
    create: hasPermission(PERM.create),
    edit: hasPermission(PERM.edit),
    delete: hasPermission(PERM.delete),
  };

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const debounceRef = useRef(null);

  const [navOpen, setNavOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [items, setItems] = useState([]);
  const [payments, setPayments] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [paymentFor, setPaymentFor] = useState(null);

  /* ---------- formulaire ---------- */
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [clients, setClients] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [originalOrders, setOriginalOrders] = useState([]);
  const [form, setForm] = useState(null);
  const [lines, setLines] = useState([]);
  const [saving, setSaving] = useState(false);
  const submitting = useRef(false);
  const returnUnits = useSalesReturnItemUnits({ userId: user?.id, companyId, warehouseId,
    originalOrderId: form?.original_order_id, active: formOpen, draft: lines });

  const salesTabs = useMemo(
    () =>
      getSalesTabsForUser(user, selectedCompany).filter(
        (t) => hasPermission(t.permission) && !t.path.endsWith("/retour-vente")
      ),
    [user, selectedCompany, hasPermission]
  );

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setAppliedSearch(search), 350);
    return () => clearTimeout(debounceRef.current);
  }, [search]);

  const load = useCallback(
    async (p) => {
      if (!warehouseId) { setRows([]); setTotal(0); return; }
      const f = FILTERS.find((x) => x.key === filter) || FILTERS[0];
      setLoading(true);
      try {
        const res = await retourVenteAPI.fetchRetourVentes({
          page: p,
          limit: PAGE,
          warehouse: warehouseId,
          ...(appliedSearch.trim() ? { search: appliedSearch.trim() } : {}),
          ...(f.status ? { payment_status: f.status } : {}),
        });
        const incoming = res.retourVentes || [];
        setRows((prev) => (p === 1 ? incoming : [...prev, ...incoming]));
        setTotal(res.totalItems || 0);
      } catch {
        message.error("Impossible de charger les retours de vente");
      } finally {
        setLoading(false);
      }
    },
    [warehouseId, appliedSearch, filter]
  );

  useEffect(() => { setPage(1); load(1); }, [load]);

  const loadMore = () => { const p = page + 1; setPage(p); load(p); };
  const reload = () => { setPage(1); load(1); };

  // Référentiels du formulaire
  useEffect(() => {
    if (!warehouseId) return;
    let alive = true;
    Promise.all([
      retourVenteAPI.fetchFournisseurs({ warehouse: warehouseId }).catch(() => []),
      retourVenteAPI.fetchTaxes().catch(() => []),
    ]).then(([c, t]) => {
      if (!alive) return;
      setClients(Array.isArray(c) ? c : []);
      setTaxes(Array.isArray(t) ? t : []);
    });
    return () => { alive = false; };
  }, [warehouseId]);

  /* ---------- détail ---------- */
  const openDetail = async (record) => {
    setDetail(record);
    setItems([]); setPayments([]);
    setDetailLoading(true);
    try {
      const [lignes, pays] = await Promise.all([
        retourVenteAPI.fetchOrderItems(String(record.id)).catch(() => []),
        fetchOrderPayments(record.id).catch(() => []),
      ]);
      setItems(asItems(lignes));
      setPayments(pays);
    } finally {
      setDetailLoading(false);
    }
  };
  const closeDetail = () => { setDetail(null); setItems([]); setPayments([]); };

  const removeReturn = (record) => {
    closeDetail();
    Modal.confirm({
      title: "Supprimer ce retour de vente ?",
      centered: true, okText: "Oui, supprimer", cancelText: "Non", okType: "danger",
      onOk: async () => {
        try {
          await retourVenteAPI.deleteRetourVente(record.id);
          message.success("Retour supprimé avec succès");
          reload();
        } catch {
          message.error("L'opération a échoué");
        }
      },
    });
  };

  /* ---------- formulaire ---------- */
  const loadOriginalOrders = async (clientId) => {
    if (!clientId) { setOriginalOrders([]); return; }
    try {
      const orders = await retourVenteAPI.fetchVentesForReturn(
        Number(clientId),
        Number(warehouseId),
        Number(companyId)
      );
      setOriginalOrders(Array.isArray(orders) ? orders : []);
    } catch {
      setOriginalOrders([]);
    }
  };

  // Création : la quantité retournable est ce qui RESTE (available_quantity), et les articles
  // entièrement retournés sont exclus (GestionRetourVente.tsx:2472-2546).
  const loadOrderItems = async (orderId) => {
    if (!orderId) { setLines([]); return; }
    try {
      const [res, returned] = await Promise.all([
        axios.get(`/api/orders/${orderId}/items`),
        retourVenteAPI.fetchReturnedQuantities(String(orderId)).catch(() => []),
      ]);
      const availableByItem = {};
      returned.forEach((r) => { availableByItem[r.original_order_item_id] = r; });

      const next = asItems(res.data)
        .map((it) => {
          const info = availableByItem[it.id] || {
            returned_quantity: 0,
            available_quantity: num(it.quantity),
          };
          const maxQuantity = Math.max(0, num(info.available_quantity));
          return {
            item_type: it.item_type || "product",
            product_id: it.product_id,
            service_id: it.service_id ?? null,
            product_name:
              it.product_name ||
              it.nom_produit ||
              it.name ||
              (it.item_type === "service" ? "Service" : "Produit"),
            quantity: maxQuantity > 0 ? Math.min(1, maxQuantity) : 0,
            original_quantity: num(it.quantity),
            returned_quantity: num(info.returned_quantity),
            max_quantity: maxQuantity,
            unit_price: num(it.unit_price),
            ...salesReturnUnitFields(it),
            discount_rate: num(it.discount_rate),
            tax_rate: num(it.tax_rate),
            tax_amount: 0,
            subtotal: 0,
            original_order_item_id: it.id,
          };
        })
        .filter((l) => l.max_quantity > 0);

      if (!next.length) message.warning("Tous les articles de cette vente ont déjà été retournés");
      setLines(next);
    } catch {
      message.error("Impossible de charger les articles de la vente d'origine");
      setLines([]);
    }
  };

  const openCreate = () => {
    if (!warehouseId) return message.warning("Veuillez sélectionner un établissement");
    setEditing(null);
    setForm({
      supplier_id: "", original_order_id: "", order_date: todayIso(),
      order_status: "completed", tax_id: "", terms_condition: "", remarks: "",
    });
    setLines([]); setOriginalOrders([]);
    setFormOpen(true);
  };

  // Édition : le plafond réintègre la quantité du retour en cours (l.1901-1912), sinon on ne
  // pourrait jamais réenregistrer un retour sans le réduire.
  const openEdit = async (record) => {
    closeDetail();
    setEditing(record);
    setForm({
      supplier_id: String(record.user_id ?? ""),
      original_order_id: String(record.original_order_id ?? ""),
      order_date: String(record.order_date || todayIso()).slice(0, 10),
      order_status: record.order_status || "completed",
      tax_id: record.tax_id ? String(record.tax_id) : "",
      terms_condition: record.terms_condition || "",
      remarks: record.notes || "",
    });
    setFormOpen(true);

    await loadOriginalOrders(record.user_id);
    try {
      const [currentRaw, original, returned] = await Promise.all([
        retourVenteAPI.fetchOrderItems(String(record.id)).catch(() => []),
        record.original_order_id
          ? axios.get(`/api/orders/${record.original_order_id}/items`)
              .then((r) => asItems(r.data)).catch(() => [])
          : Promise.resolve([]),
        record.original_order_id
          ? retourVenteAPI
              .fetchReturnedQuantities(String(record.original_order_id), record.id)
              .catch(() => [])
          : Promise.resolve([]),
      ]);

      const returnedByKey = {};
      returned.forEach((r) => {
        const key = r.original_order_item_id
          ? `line-${r.original_order_item_id}`
          : `${r.item_type || "product"}-${r.product_id || r.service_id}`;
        returnedByKey[key] = num(r.returned_quantity);
      });

      setLines(
        asItems(currentRaw).map((it) => {
          const src = findSalesReturnSourceItem(it, original);
          const originalQuantity = num(src ? src.quantity : it.quantity);
          const key = it.original_order_item_id
            ? `line-${it.original_order_item_id}`
            : `${it.item_type || "product"}-${it.product_id || it.service_id}`;
          const returnedQty = num(returnedByKey[key]);
          return {
            id: it.id,
            item_type: it.item_type || "product",
            product_id: it.product_id,
            service_id: it.service_id ?? null,
            product_name:
              it.product_name ||
              it.nom_produit ||
              it.name ||
              (it.item_type === "service" ? "Service" : "Produit"),
            quantity: num(it.quantity),
            max_quantity: Math.max(0, originalQuantity - returnedQty + num(it.quantity)),
            unit_price: num(it.unit_price),
            ...salesReturnUnitFields(it, src),
            discount_rate: num(it.discount_rate),
            tax_rate: num(it.tax_rate),
            tax_amount: num(it.tax_amount),
            subtotal: num(it.subtotal),
            original_order_item_id: it.original_order_item_id,
          };
        })
      );
    } catch {
      setLines([]);
    }
  };

  const closeForm = () => { if (!saving) { setFormOpen(false); setEditing(null); setLines([]); } };

  const globalTax = useMemo(
    () => (form?.tax_id ? taxes.find((t) => String(t.id) === String(form.tax_id)) || null : null),
    [form?.tax_id, taxes]
  );
  const totals = useMemo(() => computeTotals(lines, globalTax), [lines, globalTax]);

  const setLineField = (index, key, raw) => {
    setLines((prev) => {
      const next = [...prev];
      const line = { ...next[index] };
      if (key === "quantity") {
        let q = parseDecimal(raw);
        if (q > line.max_quantity) {
          message.warning(`La quantité ne peut pas dépasser ${fmt(line.max_quantity)} (quantité retournable)`);
          q = line.max_quantity;
        }
        line.quantity = Math.max(MIN_RETURN_QUANTITY, q);
      } else if (key === "discount_rate") {
        line.discount_rate = Math.min(100, Math.max(0, parseDecimal(raw)));
      } else if (key === "tax_rate") {
        line.tax_rate = parseDecimal(raw);
      } else {
        line[key] = Math.max(0, parseDecimal(raw));
      }
      next[index] = line;
      return next;
    });
  };

  const removeLine = (index) => setLines((prev) => prev.filter((_, i) => i !== index));

  const formValid = Boolean(form?.supplier_id) && Boolean(form?.original_order_id) && lines.length > 0;

  const submit = async () => {
    if (!formValid) return message.error("Sélectionnez une vente d'origine et au moins un article");
    if (submitting.current) return;
    submitting.current = true;
    setSaving(true);

    try {
      const computed = await returnUnits.prepareItems(totals.lines);
      const paid = editing ? num(editing.paid_amount) : 0;

      const orderData = {
        company_id: companyId,
        order_type: "sales_return",
        user_id: Number(form.supplier_id),
        warehouse_id: warehouseId,
        order_date: form.order_date,
        order_status: form.order_status,
        discount: totals.discountAmount,
        tax_id: globalTax ? globalTax.id : null,
        tax_rate: globalTax ? num(globalTax.rate) : null,
        tax_amount: totals.taxAmount,
        subtotal: totals.subTotal,
        total: totals.grandTotal,
        paid_amount: paid,
        due_amount: totals.grandTotal - paid,
        payment_status: editing ? paymentStatusOf(paid, totals.grandTotal) : "Non payé",
        terms_condition: form.terms_condition || "",
        notes: form.remarks || "",
        staff_user_id: null,
        total_items: computed.length,
        total_quantity: computed.reduce((s, l) => s + num(l.quantity), 0),
        original_order_id: Number(form.original_order_id),
        items: computed.map((l) => ({
          item_type: l.item_type || "product",
          product_id: l.product_id,
          service_id: l.item_type === "service" ? l.service_id : null,
          item_name: l.product_name,
          quantity: l.quantity,
          unit_price: l.unit_price,
          unit_id: l.unit_id,
          discount_rate: l.discount_rate,
          tax_rate: l.tax_rate,
          tax_amount: l.tax_amount,
          subtotal: l.subtotal,
          original_order_item_id: l.original_order_item_id,
          id: editing && l.id ? l.id : undefined,
        })),
      };

      if (editing) {
        await retourVenteAPI.updateRetourVente(editing.id, orderData);
        message.success("Retour de vente mis à jour avec succès");
      } else {
        await retourVenteAPI.createRetourVente(orderData);
        message.success("Retour de vente enregistré avec succès");
      }
      setFormOpen(false); setEditing(null); setLines([]);
      reload();
    } catch (err) {
      message.error(err?.response?.data?.error || err?.message || "Erreur lors de l'enregistrement du retour");
    } finally {
      submitting.current = false;
      setSaving(false);
    }
  };

  /* ---------- rendu ---------- */
  if (!warehouseId) {
    return (
      <ProductScreen>
        <Appbar title="Retours de vente" onMenu={onMenu} />
        <div className="body">
          <EmptyState icon="warehouse" title="Aucun établissement sélectionné"
            text="Choisissez un établissement pour voir les retours de vente." />
        </div>
      </ProductScreen>
    );
  }

  const dueAmount = detail ? num(detail.total) - num(detail.paid_amount) : 0;

  return (
    <ProductScreen>
      <Appbar
        title="Retours de vente" onMenu={onMenu}
        subtitle={`${fmt(total)} retour${total > 1 ? "s" : ""}`}
        action={
          salesTabs.length ? (
            <button className="iconbtn" aria-label="Aller à un autre onglet" onClick={() => setNavOpen(true)}>
              <Ico name="list-ordered" />
            </button>
          ) : null
        }
      />

      <div className="appbar" style={{ borderTop: 0, paddingTop: 0, gap: 12 }}>
        <SearchBar value={search} onChange={setSearch} placeholder="Rechercher un n° de facture…" />
        <Chips value={filter} onChange={setFilter} options={FILTERS.map((f) => ({ key: f.key, label: f.label }))} />
      </div>

      <div className="body">
        {loading && !rows.length ? (
          <EmptyState loading title="Chargement des retours" text="Synchronisation en cours." />
        ) : !rows.length ? (
          <EmptyState
            icon="rotate-ccw"
            title={appliedSearch || filter !== "all" ? "Aucun résultat" : "Aucun retour de vente"}
            text={appliedSearch || filter !== "all"
              ? "Aucun retour ne correspond à vos filtres."
              : "Créez un retour à partir d'une vente livrée ou complétée."}
          />
        ) : (
          <div className="list">
            {rows.map((r) => {
              const pay = paymentPill(r.payment_status);
              const ord = orderPill(r);
              return (
                <button key={r.id} className="rcard" onClick={() => openDetail(r)}>
                  <Mono name={r.invoice_number || "R"} />
                  <div className="rcard__main">
                    <div className="rcard__name">{r.invoice_number}</div>
                    <div className="rcard__meta">
                      {r.is_deleted === 1 ? <Pill tone="danger" label="Supprimé" /> : null}
                      <Pill tone={pay.tone} label={pay.label} />
                      <Pill tone={ord.tone} label={ord.label} />
                    </div>
                    <div className="rcard__meta">
                      <span className="rcard__sub">{fmtDate(r.order_date)} · {clientNameOf(r, clients)}</span>
                    </div>
                  </div>
                  <span className="rcard__price">
                    {fmtMoney(r.total)}
                    <small> remboursé {fmtMoney(r.paid_amount)}</small>
                  </span>
                </button>
              );
            })}

            {rows.length < total ? (
              <button className="btn btn--ghost" style={{ width: "100%" }} onClick={loadMore} disabled={loading}>
                <Ico name={loading ? "loader-circle" : "list-ordered"} className={loading ? "spin" : ""} />
                Charger plus ({rows.length}/{total})
              </button>
            ) : null}
          </div>
        )}
      </div>

      {can.create ? <Fab onClick={openCreate} label="Nouveau retour" /> : null}

      <Sheet open={navOpen} onClose={() => setNavOpen(false)} title="Aller à…" subtitle="Autres écrans du module Ventes.">
        {salesTabs.map((t) => (
          <ActionRow key={t.key} icon="chevron-right" label={t.label}
            onClick={() => { setNavOpen(false); navigate(t.path); }} />
        ))}
      </Sheet>

      {/* feuille : détail */}
      <Sheet
        open={Boolean(detail)} onClose={closeDetail}
        title={detail?.invoice_number}
        subtitle={detail ? clientNameOf(detail, clients) : undefined}
      >
        {detail ? (
          <>
            <DetailRow label="Date" value={fmtDate(detail.order_date)} />
            <DetailRow label="Établissement" value={detail.warehouse_name || "—"} />
            <DetailRow label="Statut" value={orderPill(detail).label} />
            <DetailRow label="Statut paiement" value={paymentPill(detail.payment_status).label} />
            <DetailRow label="Total" value={fmtMoney(detail.total)} />
            <DetailRow label="Remboursé" value={fmtMoney(detail.paid_amount)} />
            <DetailRow label="Restant" value={fmtMoney(Math.max(0, dueAmount))} />

            <div className="field__lbl" style={{ margin: "16px 0 8px" }}>Articles</div>
            {detailLoading && !items.length ? (
              <EmptyState loading title="Chargement…" />
            ) : !items.length ? (
              <Note>Aucun article à afficher.</Note>
            ) : (
              items.map((it, i) => (
                <DetailRow key={it.id ?? i} label={it.product_name || it.nom_produit || it.name}
                  value={`${fmt(it.quantity)} × ${fmtMoney(it.unit_price)}`} />
              ))
            )}

            <div className="field__lbl" style={{ margin: "16px 0 8px" }}>Remboursements</div>
            {!payments.length ? (
              <Note>Aucun remboursement enregistré pour ce retour.</Note>
            ) : (
              payments.map((p, i) => (
                <DetailRow key={p.id ?? i}
                  label={`${p.payment_number || `Paiement ${i + 1}`}${p.payment_mode_name ? ` · ${p.payment_mode_name}` : ""}`}
                  value={fmtMoney(p.amount)} />
              ))
            )}

            <div style={{ marginTop: 16 }}>
              <SalesReturnDocumentAction order={detail} kind="credit-note" block />
              {canRefund({ ...detail, due_amount: dueAmount }) && detail.is_deleted !== 1 ? (
                <ActionRow icon="banknote" label="Ajouter un remboursement" onClick={() => setPaymentFor(detail)} />
              ) : null}
              {can.edit && !isEditBlocked(detail) ? (
                <ActionRow icon="pencil" label="Modifier le retour" onClick={() => openEdit(detail)} />
              ) : null}
              {canDeleteReturn(detail, can.delete) ? (
                <ActionRow icon="trash-2" label="Supprimer le retour" danger onClick={() => removeReturn(detail)} />
              ) : null}
            </div>
          </>
        ) : null}
      </Sheet>

      {/* feuille : formulaire */}
      <Sheet
        open={formOpen} onClose={closeForm}
        title={editing ? "Modifier le retour" : "Nouveau retour de vente"}
        subtitle="Les articles proviennent de la vente d'origine."
      >
        {form ? (
          <>
            <Field label="Client *">
              <SelectInput
                value={form.supplier_id}
                onChange={(v) => {
                  setForm((f) => ({ ...f, supplier_id: v || "", original_order_id: "" }));
                  setLines([]);
                  loadOriginalOrders(v);
                }}
                options={clients.map((c) => ({ value: String(c.id), label: c.name }))}
                placeholder="Sélectionner un client"
              />
            </Field>

            <Field label="Vente d'origine *" hint="ventes terminées ou livrées">
              <SelectInput
                value={form.original_order_id}
                onChange={(v) => { setForm((f) => ({ ...f, original_order_id: v || "" })); loadOrderItems(v); }}
                options={originalOrders.map((o) => ({
                  value: String(o.id),
                  label: `${o.invoice_number} — ${fmtDate(o.order_date)} — ${fmtMoney(o.total)}`,
                }))}
                placeholder={form.supplier_id ? "Sélectionner une vente" : "Choisissez d'abord un client"}
              />
            </Field>

            <Field label="Date du retour">
              <div className="ctrl">
                <input type="date" value={form.order_date}
                  onChange={(e) => setForm((f) => ({ ...f, order_date: e.target.value }))} />
              </div>
            </Field>

            <Field label="Statut du retour">
              <SelectInput
                value={form.order_status}
                onChange={(v) => setForm((f) => ({ ...f, order_status: v || "completed" }))}
                options={ORDER_STATUS_OPTIONS}
                placeholder="Sélectionner un statut"
              />
            </Field>

            <Field label="Taxe globale" hint="écrase la taxe de chaque ligne">
              <SelectInput
                value={form.tax_id}
                onChange={(v) => setForm((f) => ({ ...f, tax_id: v || "" }))}
                options={taxes.map((t) => ({ value: String(t.id), label: `${t.name} (${t.rate} %)` }))}
                placeholder="Aucune (taxes par ligne)"
              />
            </Field>

            <div className="field__lbl" style={{ margin: "16px 0 8px" }}>
              Articles retournés {lines.length ? <small>({lines.length})</small> : null}
            </div>
            {returnUnits.error && <Alert type="warning" showIcon
              message="Les unités de la vente n'ont pas pu être vérifiées. Votre saisie est conservée."
              action={<Button onClick={() => { void returnUnits.refresh({ force: true }).catch(() => {}); }}>Réessayer</Button>} />}
            {!lines.length ? (
              <Note>Sélectionnez une vente d'origine pour charger ses articles retournables.</Note>
            ) : (
              lines.map((line, i) => {
                const c = totals.lines[i];
                return (
                  <div key={line.original_order_item_id ?? i} className="preview" style={{ display: "block", marginBottom: 12 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                      <div className="preview__name">{line.product_name}</div>
                      <button className="iconbtn" aria-label="Retirer" onClick={() => removeLine(i)}>
                        <Ico name="trash-2" />
                      </button>
                    </div>
                    <Field label={`Quantité (max ${fmt(line.max_quantity)})`}>
                      <TextInput value={String(line.quantity)} inputMode="decimal"
                        onChange={(v) => setLineField(i, "quantity", v)} />
                    </Field>
                    <Field label="Prix unitaire">
                      <TextInput value={String(line.unit_price)} inputMode="decimal"
                        onChange={(v) => setLineField(i, "unit_price", v)} />
                    </Field>
                    <Field label="Remise (%)">
                      <TextInput value={String(line.discount_rate)} inputMode="decimal"
                        onChange={(v) => setLineField(i, "discount_rate", v)} />
                    </Field>
                    {!globalTax ? (
                      <Field label="Taxe de la ligne">
                        <SelectInput
                          value={String(line.tax_rate)}
                          onChange={(v) => setLineField(i, "tax_rate", v ?? 0)}
                          options={[
                            { value: "0", label: "Aucune (0 %)" },
                            ...taxes.map((t) => ({ value: String(t.rate), label: `${t.name} (${t.rate} %)` })),
                          ]}
                        />
                      </Field>
                    ) : null}
                    <DetailRow label="Sous-total" value={fmtMoney(c?.subtotal)} />
                  </div>
                );
              })
            )}

            {lines.length ? (
              <>
                <div className="field__lbl" style={{ margin: "16px 0 8px" }}>Totaux</div>
                <DetailRow label="Total HT" value={fmtMoney(totals.subTotal)} />
                <DetailRow label="Total remises" value={fmtMoney(totals.discountAmount)} />
                <DetailRow label="Total taxes" value={fmtMoney(totals.taxAmount)} />
                <DetailRow label="Total TTC" value={fmtMoney(totals.grandTotal)} />
              </>
            ) : null}

            <Field label="Conditions" hint="facultatif">
              <TextAreaInput value={form.terms_condition} rows={2}
                onChange={(v) => setForm((f) => ({ ...f, terms_condition: v }))} />
            </Field>
            <Field label="Remarques" hint="facultatif">
              <TextAreaInput value={form.remarks} rows={2}
                onChange={(v) => setForm((f) => ({ ...f, remarks: v }))} />
            </Field>

            <div className="sheet__actions">
              <button className="btn btn--ghost" onClick={closeForm} disabled={saving}>Annuler</button>
              <button className="btn btn--primary" onClick={submit} disabled={saving || !formValid}>
                <Ico name={saving ? "loader-circle" : "check"} className={saving ? "spin" : ""} />
                {editing ? "Mettre à jour" : "Enregistrer"}
              </button>
            </div>
          </>
        ) : null}
      </Sheet>

      <PaymentFormModal
        visible={Boolean(paymentFor)}
        order={paymentFor}
        onClose={() => setPaymentFor(null)}
        onPaymentAdded={() => { setPaymentFor(null); closeDetail(); reload(); }}
      />
    </ProductScreen>
  );
}
